import { api } from '@ppwr/convex/_generated/api'
import type { LwCourseSummary } from '@ppwr/convex/learnworlds/types'
import { useAction } from 'convex/react'
import { useRouter } from 'expo-router'
import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ActivityIndicator, Image, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'

import Menu from '../../../assets/mainHeader/Menu.png'
import { useDrawerStore } from '@/components/Drawer/Drawer'
import { useIsFocused } from '@react-navigation/native'

import { MainHeader } from '../../components/MainHeader/MainHeader'
import { DS } from '../../design-system'
import { useStore } from '../../models'
import { useRemoteConfig } from '../../../module/remote-config/useRemoteConfig'
import { courseCategories, filterByCategory, selectLearnCourses } from './learnCourses'

/** PP-4829: mirrors the Read tab's article row — image left, bold title and description right. */
const CourseCard = ({ course, onPress }: { course: LwCourseSummary; onPress: () => void }) => (
  <TouchableOpacity style={styles.card} onPress={onPress} activeOpacity={0.8}>
    {course.image ? (
      <Image source={{ uri: course.image }} style={styles.cardImage} resizeMode="cover" />
    ) : (
      <View style={[styles.cardImage, styles.cardImagePlaceholder]}>
        <DS.Text variant="Heading 4/Regular" color="Brand/Dark Grey">
          {course.title.slice(0, 1).toUpperCase()}
        </DS.Text>
      </View>
    )}
    <View style={styles.cardBody}>
      <DS.Text variant="Body Medium/Medium" numberOfLines={3}>
        {course.title}
      </DS.Text>
      {course.description ? (
        <DS.Text variant="Body Small/Regular" color="Brand/Dark Grey" numberOfLines={2}>
          {course.description}
        </DS.Text>
      ) : null}
    </View>
  </TouchableOpacity>
)

const CategoryChip = ({ label, selected, onPress }: { label: string; selected: boolean; onPress: () => void }) => (
  <TouchableOpacity
    style={[styles.chip, selected && styles.chipSelected]}
    onPress={onPress}
    activeOpacity={0.8}
    accessibilityRole="button"
    accessibilityState={{ selected }}
  >
    <DS.Text variant="Body Small/Medium" color={selected ? 'Global/White' : 'Brand/Dark Grey'} numberOfLines={1}>
      {label}
    </DS.Text>
  </TouchableOpacity>
)

/** `asTab` renders it as the Learn tab root: drawer menu instead of a back button. */
export const CoursesScreen = ({ asTab = false }: { asTab?: boolean }) => {
  const router = useRouter()
  const { toggleDrawer } = useDrawerStore()
  const { setLastVisitedTab } = useStore().session
  const isFocused = useIsFocused()
  const listCourses = useAction(api.learnworlds.actions.listCourses)

  const [courses, setCourses] = useState<LwCourseSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const coursesCategory = useRemoteConfig((state) => state.courses_category)
  const visibleCourses = useMemo(
    () => (courses ? selectLearnCourses(courses, coursesCategory) : null),
    [courses, coursesCategory]
  )
  const categories = useMemo(() => courseCategories(visibleCourses ?? []), [visibleCourses])
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  // A remote-config change can drop the selected category; fall back to "All" when it does.
  const activeCategory = selectedCategory && categories.includes(selectedCategory) ? selectedCategory : null
  const shownCourses = useMemo(
    () => (visibleCourses ? filterByCategory(visibleCourses, activeCategory) : null),
    [visibleCourses, activeCategory]
  )

  const load = useCallback(() => {
    setError(null)
    setCourses(null)
    listCourses({})
      .then(setCourses)
      .catch((e: Error) => setError(e.message))
  }, [listCourses])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (asTab && isFocused) setLastVisitedTab('learning')
  }, [asTab, isFocused, setLastVisitedTab])

  return (
    // As a tab, the tab bar already pads the bottom inset
    <SafeAreaView style={styles.container} edges={asTab ? ['top', 'left', 'right'] : undefined}>
      {!asTab || isFocused ? <StatusBar animated style={'dark' as StatusBarStyle} hidden={false} /> : null}
      {asTab ? (
        <MainHeader
          hideLeftDefaultIcon
          title="Courses"
          titleStyle={DS.typography['Heading 2/Regular']}
          rightIcon={Menu}
          rightPress={() => toggleDrawer()}
        />
      ) : (
        <MainHeader
          leftPress={() => router.back()}
          title="Courses"
          titleStyle={{ color: DS.palette['Global/Black'], marginLeft: DS.spacing[12] }}
        />
      )}
      {categories.length > 1 ? (
        <View>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.chipRow}
            directionalLockEnabled
          >
            <CategoryChip label="All" selected={activeCategory === null} onPress={() => setSelectedCategory(null)} />
            {categories.map((category) => (
              <CategoryChip
                key={category}
                label={category}
                selected={activeCategory === category}
                onPress={() => setSelectedCategory(category)}
              />
            ))}
          </ScrollView>
        </View>
      ) : null}
      <ScrollView contentContainerStyle={[styles.scrollContent, styles.webWrapper]}>
        {courses === null && error === null ? <ActivityIndicator style={styles.loader} /> : null}

        {error ? (
          <View style={styles.errorBox}>
            <DS.Text variant="Body Small/Regular" color="Utility/Error">
              {error}
            </DS.Text>
            <TouchableOpacity onPress={load}>
              <DS.Text variant="Body Small/Medium">Try again</DS.Text>
            </TouchableOpacity>
          </View>
        ) : null}

        {shownCourses?.map((course) => (
          <CourseCard key={course.id} course={course} onPress={() => router.push(`/courses/${course.slug}`)} />
        ))}

        {shownCourses?.length === 0 ? (
          <DS.Text variant="Body Small/Regular" color="Brand/Dark Grey">
            No courses available yet.
          </DS.Text>
        ) : null}
      </ScrollView>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  scrollContent: {
    padding: DS.spacing[24],
    paddingBottom: DS.spacing[48],
    gap: DS.spacing[16]
  },
  webWrapper: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center'
  },
  chipRow: {
    gap: DS.spacing[8],
    paddingHorizontal: DS.spacing[24],
    paddingTop: DS.spacing[16]
  },
  chip: {
    backgroundColor: DS.palette['Global/White'],
    borderColor: DS.palette['Brand/Light Grey'],
    borderRadius: 40,
    borderWidth: 1,
    paddingHorizontal: DS.spacing[16],
    paddingVertical: DS.spacing[8]
  },
  chipSelected: {
    backgroundColor: DS.palette['Brand/Maroon'],
    borderColor: DS.palette['Brand/Maroon']
  },
  loader: {
    marginTop: DS.spacing[48]
  },
  errorBox: {
    gap: DS.spacing[8]
  },
  card: {
    flexDirection: 'row',
    gap: DS.spacing[16],
    backgroundColor: DS.palette['Global/White'],
    borderRadius: DS.spacing[8],
    overflow: 'hidden',
    padding: DS.spacing[12]
  },
  cardImage: {
    width: 96,
    height: 96,
    borderRadius: DS.spacing[6],
    backgroundColor: DS.palette['Brand/Light Grey']
  },
  cardImagePlaceholder: {
    alignItems: 'center',
    justifyContent: 'center'
  },
  cardBody: {
    flex: 1,
    gap: DS.spacing[4],
    justifyContent: 'center'
  }
})
